import type { BlogPost, BlogCategory, BlogFilter, BlogPagination } from '../../types/blog';
import { BLOG_CATEGORIES } from '../../data/blog/categories';
import { AutoBlogGenerator } from './auto-generator';
import { createSlug } from '../../utils/blog';

// Sample blog posts for demonstration
const SAMPLE_POSTS: BlogPost[] = [
  {
    id: '1',
    title: 'Understanding Dollar to Naira Black Market Rate Dynamics',
    content: `# Understanding Dollar to Naira Black Market Rate Dynamics

The Dollar to Naira black market rate, also known as the parallel market rate, represents the unofficial exchange rate determined by market forces outside the formal banking system.

## Key Factors Affecting Rates

### Supply and Demand
The fundamental driver of black market rates is the balance between dollar supply and demand in the informal market.

### Economic Policies
CBN policies and foreign exchange regulations significantly impact parallel market dynamics.

### Global Economic Conditions
International economic events and US monetary policy affect dollar availability and pricing.

## Current Market Analysis

Today's Dollar to Naira black market rate reflects ongoing economic pressures and market sentiment. Traders should monitor:

- CBN intervention activities
- Foreign investment flows
- Oil price movements
- Political stability factors

## Trading Considerations

When dealing with exchange rates:

1. **Verify legitimacy** of exchange operators
2. **Compare rates** across multiple sources
3. **Understand risks** involved in parallel market transactions
4. **Stay informed** about regulatory changes

*This analysis is for educational purposes only and does not constitute financial advice.*`,
    excerpt: 'Comprehensive analysis of factors affecting the Dollar to Naira black market rate and parallel market dynamics in Nigeria.',
    slug: 'understanding-dollar-naira-black-market-rate-dynamics',
    category: BLOG_CATEGORIES[1], // Exchange Rates
    tags: ['black-market', 'dollar-naira', 'parallel-market', 'market-analysis'],
    author: 'NigeriaFx Research Team',
    publishedAt: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000),
    updatedAt: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000),
    featuredImage: 'https://images.unsplash.com/photo-1639322537228-f710d846310a',
    readTime: 6,
    isPublished: true,
    views: 1250,
    seoTitle: 'Dollar to Naira Black Market Rate Analysis | Parallel Market Dynamics',
    seoDescription: 'Understanding Dollar to Naira black market rate factors, parallel market dynamics, and trading considerations in Nigeria.'
  },
  {
    id: '2',
    title: "Official Exchange Rate Nigeria vs Market Reality",
    content: `# CBN Official Exchange Rate Nigeria: Policy Impact Analysis

The Central Bank of Nigeria official exchange rate serves as the benchmark for formal financial transactions and reflects the monetary authority's policy stance.

## Recent Policy Changes

### Exchange Rate Unification
The Central Bank's move toward exchange rate unification has significant implications for market dynamics.

### Market Intervention Strategies
Understanding how Central Bank interventions affect both official and parallel market rates.

## Impact on Different Sectors

### Import/Export Businesses
Official rates directly affect the cost of international trade transactions.

### Foreign Investment
Exchange rate policies influence foreign direct investment flows into Nigeria.

### Individual Traders
How policy changes affect personal forex transactions and remittances.

## Comparing Official vs Parallel Rates

The spread between official exchange rate Nigeria and parallel market rates indicates:

- Market confidence levels
- Policy effectiveness
- Economic stability indicators
- Future rate direction signals

## Key Takeaways

1. **Monitor policy announcements** from Central Bank regularly
2. **Understand rate implications** for your specific needs
3. **Plan transactions** around policy cycles
4. **Stay compliant** with official regulations

*Always use licensed operators for official rate transactions.*`,
    excerpt: 'Analysis of official exchange rate Nigeria policies and their impact on formal and informal forex markets.',
    slug: 'cbn-official-exchange-rate-nigeria-policy-impact',
    category: BLOG_CATEGORIES[4], // Official Updates
    tags: ['official-rate', 'monetary-policy', 'nigeria-forex'],
    author: 'NigeriaFx Policy Team',
    publishedAt: new Date(Date.now() - 5 * 24 * 60 * 60 * 1000),
    updatedAt: new Date(Date.now() - 5 * 24 * 60 * 60 * 1000),
    featuredImage: 'https://images.unsplash.com/photo-1611974789855-9c2a0a7236a3',
    readTime: 8,
    isPublished: true,
    views: 890,
    seoTitle: 'Official Exchange Rate Nigeria | Central Bank Policy Analysis',
    seoDescription: 'Analysis of official exchange rate Nigeria policies, market impact, and implications for forex traders and businesses.'
  },
  {
    id: '3',
    title: 'Euro to Naira Exchange Rate Today: Market Trends & Predictions',
    content: `# Euro to Naira Exchange Rate Today: Market Trends & Predictions

The Euro to Naira exchange rate today reflects both European economic conditions and Nigerian market dynamics, making it a key pair for international traders.

## Current EUR/NGN Analysis

### Market Position
Today's Euro to Naira exchange rate stands as one of the most watched pairs in the Nigerian forex market.

### Recent Performance
The EUR/NGN pair has shown resilience despite global economic uncertainties.

## Factors Influencing EUR/NGN Rates

### European Economic Indicators
- ECB monetary policy decisions
- Eurozone inflation data
- Economic growth projections
- Political stability in major EU economies

### Nigerian Market Conditions
- Local demand for Euros
- Trade relationships with European countries
- Tourism and business travel patterns
- Investment flows

## Trading Opportunities

### Technical Analysis
Current chart patterns suggest potential opportunities for informed traders.

### Fundamental Outlook
Economic fundamentals support medium-term EUR/NGN stability.

## Risk Considerations

When trading Euro to Naira exchange rate today:

1. **Monitor ECB announcements** for policy changes
2. **Track Nigerian economic data** releases
3. **Consider global risk sentiment** impacts
4. **Maintain proper risk management** protocols

*Euro to Naira exchange rate today information is for educational purposes only.*`,
    excerpt: 'Daily analysis of Euro to Naira exchange rate trends, market factors, and trading opportunities in the Nigerian forex market.',
    slug: 'euro-naira-exchange-rate-today-trends-predictions',
    category: BLOG_CATEGORIES[1], // Exchange Rates
    tags: ['euro-naira', 'eur-ngn', 'daily-analysis', 'market-trends'],
    author: 'NigeriaFx EUR/NGN Specialist',
    publishedAt: new Date(Date.now() - 1 * 24 * 60 * 60 * 1000),
    updatedAt: new Date(Date.now() - 1 * 24 * 60 * 60 * 1000),
    featuredImage: 'https://images.unsplash.com/photo-1642790106117-e829e14a795f',
    readTime: 5,
    isPublished: true,
    views: 675,
    seoTitle: 'Euro to Naira Exchange Rate Today | EUR/NGN Market Analysis & Trends',
    seoDescription: 'Get today\'s Euro to Naira exchange rate analysis, market trends, and trading predictions. Live EUR/NGN rates and expert insights.'
  }
];

class BlogService {
  private posts: BlogPost[] = [...SAMPLE_POSTS];
  private autoGenerator = AutoBlogGenerator.getInstance();

  async getAllPosts(filter?: BlogFilter, pagination?: BlogPagination): Promise<{
    posts: BlogPost[];
    pagination: BlogPagination;
  }> {
    let filteredPosts = [...this.posts, ...this.autoGenerator.getGeneratedPosts()];

    // Apply filters
    if (filter?.category) {
      filteredPosts = filteredPosts.filter(post => post.category.slug === filter.category);
    }

    if (filter?.tag) {
      filteredPosts = filteredPosts.filter(post => post.tags.includes(filter.tag!));
    }

    if (filter?.search) {
      const searchTerm = filter.search.toLowerCase();
      filteredPosts = filteredPosts.filter(post => 
        post.title.toLowerCase().includes(searchTerm) ||
        post.excerpt.toLowerCase().includes(searchTerm) ||
        post.tags.some(tag => tag.toLowerCase().includes(searchTerm))
      );
    }

    // Apply sorting
    if (filter?.sortBy) {
      filteredPosts.sort((a, b) => {
        let aValue: any, bValue: any;
        
        switch (filter.sortBy) {
          case 'date':
            aValue = a.publishedAt.getTime();
            bValue = b.publishedAt.getTime();
            break;
          case 'views':
            aValue = a.views || 0;
            bValue = b.views || 0;
            break;
          case 'title':
            aValue = a.title.toLowerCase();
            bValue = b.title.toLowerCase();
            break;
          default:
            return 0;
        }

        if (filter.sortOrder === 'asc') {
          return aValue > bValue ? 1 : -1;
        }
        return aValue < bValue ? 1 : -1;
      });
    } else {
      // Default sort by date (newest first)
      filteredPosts.sort((a, b) => b.publishedAt.getTime() - a.publishedAt.getTime());
    }

    // Apply pagination
    const page = pagination?.page || 1;
    const limit = pagination?.limit || 12;
    const startIndex = (page - 1) * limit;
    const endIndex = startIndex + limit;
    const paginatedPosts = filteredPosts.slice(startIndex, endIndex);

    return {
      posts: paginatedPosts,
      pagination: {
        page,
        limit,
        total: filteredPosts.length,
        totalPages: Math.ceil(filteredPosts.length / limit)
      }
    };
  }

  async getPostBySlug(slug: string): Promise<BlogPost | null> {
    const allPosts = [...this.posts, ...this.autoGenerator.getGeneratedPosts()];
    return allPosts.find(post => post.slug === slug) || null;
  }

  async getFeaturedPosts(limit: number = 3): Promise<BlogPost[]> {
    const allPosts = [...this.posts, ...this.autoGenerator.getGeneratedPosts()];
    return allPosts
      .filter(post => post.isPublished)
      .sort((a, b) => (b.views || 0) - (a.views || 0))
      .slice(0, limit);
  }

  async getRelatedPosts(post: BlogPost, limit: number = 3): Promise<BlogPost[]> {
    const allPosts = [...this.posts, ...this.autoGenerator.getGeneratedPosts()];
    return allPosts
      .filter(p => 
        p.id !== post.id && 
        p.isPublished && 
        (p.category.id === post.category.id || 
         p.tags.some(tag => post.tags.includes(tag)))
      )
      .slice(0, limit);
  }

  async addPost(post: BlogPost): Promise<void> {
    this.posts.unshift(post);
    // Keep only last 100 posts to prevent memory issues
    if (this.posts.length > 100) {
      this.posts = this.posts.slice(0, 100);
    }
  }

  getCategories(): BlogCategory[] {
    return BLOG_CATEGORIES;
  }

  async generateContentFromRates(rates: ExchangeRate[]): Promise<void> {
    // Check for significant rate changes
    const significantChanges = rates.filter(rate => Math.abs(rate.change) > 2);
    
    if (significantChanges.length > 0) {
      for (const rate of significantChanges) {
        const breakingPost = await this.autoGenerator.generateBreakingNewsPost(rate, 2);
        this.autoGenerator.addGeneratedPost(breakingPost);
      }
    }

    // Generate daily summary if none exists for today
    const today = new Date().toISOString().split('T')[0];
    const existingDailyPost = this.autoGenerator.getGeneratedPosts()
      .find(post => post.id.includes(`daily-${today}`));

    if (!existingDailyPost) {
      const dailyPost = await this.autoGenerator.generateDailyRatePost(rates);
      this.autoGenerator.addGeneratedPost(dailyPost);
    }
  }
}

export const blogService = new BlogService();